import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useAuth } from '../AuthContext';
import { styled } from '@mui/system';
import { Button, TextField, Typography, Box, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const FormContainer = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  border: '1px solid #ccc',
  borderRadius: '8px',
  width: '100%',
  maxWidth: '600px',
  margin: '0 auto',
});

const SectionTitle = styled(Typography)({
  margin: '20px 0 10px 0',
  fontSize: '1.5rem',
  fontWeight: 'bold',
});

const InputField = styled(TextField)({
  marginBottom: '15px',
  width: '100%',
});

const AddButton = styled(Button)({
  marginTop: '10px',
  marginBottom: '20px',
});

const SubmitButton = styled(Button)({
  marginTop: '20px',
  backgroundColor: '#007BFF',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#0056b3',
  },
});

const NewMember = () => {
  const { register, handleSubmit, control, reset } = useForm();
  const [photo, setPhoto] = useState(null);
  const [photoURL, setPhotoURL] = useState(null);
  const { token, uid } = useAuth();

  const { fields: languageFields, append: appendLanguage, remove: removeLanguage } = useFieldArray({
    control,
    name: 'languages',
  });
  const { fields: linkFields, append: appendLink, remove: removeLink } = useFieldArray({
    control,
    name: 'links',
  });
  const { fields: skillFields, append: appendSkill, remove: removeSkill } = useFieldArray({
    control,
    name: 'skills',
  });

  const onSubmit = async (data) => {
    const formData = new FormData();

    formData.append('Name', data.userName);
    formData.append('UserId', uid);
    formData.append('Summary', data.summary);
    formData.append('Position', data.position);
    formData.append('Photo', photo);

    formData.append('Contacts[PhoneNumber]', data.contacts.phoneNumber);
    formData.append('Contacts[Email]', data.contacts.email);

    formData.append('Education[UniversityName]', data.education.universityName);
    formData.append('Education[Degree]', data.education.degree);

    data.languages.forEach((language, index) => {
      formData.append(`Languages[${index}][LanguageTitle]`, language.languageTitle);
      formData.append(`Languages[${index}][Level]`, language.level);
    });

    data.links.forEach((link, index) => {
      formData.append(`Links[${index}][TypeName]`, link.typeName);
      formData.append(`Links[${index}][LinkAddress]`, link.linkAddress);
    });

    data.skills.forEach((skill, index) => {
      formData.append(`Skills[${index}][SkillTitle]`, skill.skillTitle);
    });

    try {
      const response = await fetch('https://sirius.runasp.net/api/team', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        console.log('Member created successfully');
        reset();
        setPhoto(null);
        setPhotoURL(null);
        window.location.reload()
      } else {
        console.error('Failed to create member');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhoto(file);
    setPhotoURL(URL.createObjectURL(file)); // Create a URL for the selected file
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <InputField label="User Name" {...register('userName')} required />
      <InputField label="Summary" {...register('summary')} required multiline rows={4} />
      <InputField label="Position" {...register('position')} required />

      <Box sx={{ marginBottom: '15px' }}>
        <input type="file" onChange={handlePhotoChange} required />
      </Box>
      {photoURL && (
        <img
          src={photoURL}
          style={{
            width: '150px',
            height: '150px',
            border: '2px solid #fff',
            borderRadius: '50%',
            marginBottom: '15px',
          }}
          alt="Selected"
        />
      )}

      <SectionTitle>Contacts</SectionTitle>
      <InputField label="Phone Number" {...register('contacts.phoneNumber')} required />
      <InputField label="Email" {...register('contacts.email')} required type="email" />

      <SectionTitle>Education</SectionTitle>
      <InputField label="University Name" {...register('education.universityName')} required />
      <InputField label="Degree" {...register('education.degree')} required />

      <SectionTitle>Languages</SectionTitle>
      {languageFields.map((field, index) => (
        <Box key={field.id} sx={{ display: 'flex', alignItems: 'center' }}>
          <InputField
            label="Language Title"
            {...register(`languages.${index}.languageTitle`)}
            required
          />
          <InputField label="Level" {...register(`languages.${index}.level`)} required />
          <IconButton onClick={() => removeLanguage(index)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <AddButton variant="outlined" onClick={() => appendLanguage({ languageTitle: '', level: '' })}>
        Add Language
      </AddButton>

      <SectionTitle>Links</SectionTitle>
      {linkFields.map((field, index) => (
        <Box key={field.id} sx={{ display: 'flex', alignItems: 'center' }}>
          <InputField label="Link Type" {...register(`links.${index}.typeName`)} required />
          <InputField label="Link Address" {...register(`links.${index}.linkAddress`)} required />
          <IconButton onClick={() => removeLink(index)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <AddButton variant="outlined" onClick={() => appendLink({ typeName: '', linkAddress: '' })}>
        Add Link
      </AddButton>

      <SectionTitle>Skills</SectionTitle>
      {skillFields.map((field, index) => (
        <Box key={field.id} sx={{ display: 'flex', alignItems: 'center' }}>
          <InputField label="Skill Title" {...register(`skills.${index}.skillTitle`)} required />
          <IconButton onClick={() => removeSkill(index)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}
      <AddButton variant="outlined" onClick={() => appendSkill({ skillTitle: '' })}>
        Add Skill
      </AddButton>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <SubmitButton type="submit" variant="contained">
          Create Member
        </SubmitButton>
      </Box>
    </FormContainer>
  );
};

export default NewMember;
